import {
  useGenerateReceiptBatchPreview,
  useGetReceiptBatchListPhoenix,
} from '@pflegenavi/frontend/api-nursing-home';
import { useNursingHomeContext } from '@pflegenavi/frontend/nursing-home-context';
import { ReceiptBatchJobState } from '@pflegenavi/shared/api';
import type { UrlTypeNameMapping } from '@pflegenavi/web-components';
import { useErrorSnackbar } from '@pflegenavi/web-components';
import { keepPreviousData } from '@tanstack/react-query';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { ReceiptTypeDropdown } from '../ReceiptTypeDropdown';
import type { ReceiptBatchTableFilters } from './ReceiptBatchOverviewTableView';
import {
  ReceiptBatchOverviewTableView,
  ReceiptBatchStatus,
} from './ReceiptBatchOverviewTableView';
import { ReceiptBatchTableEnlargeGallery } from './ReceiptBatchTableEnlargeGallery';
import { useSelectedEmployeeIds } from '@pflegenavi/frontend/nursing-home-context';
interface ReceiptBatchOverviewTableProps {
  serviceProviderIds?: string[];
  showReceiptTypeDropdown?: boolean;
  filters?: ReceiptBatchTableFilters[];
  recurringItemId?: string;
  dateFrom?: Date;
  dateTo?: Date;
}

export const ReceiptBatchOverviewTable: FC<ReceiptBatchOverviewTableProps> = ({
  serviceProviderIds,
  showReceiptTypeDropdown,
  filters,
  recurringItemId,
  dateFrom,
  dateTo,
}) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [query, setQuery] = useState<string>('');
  const { selectedNursingHome } = useNursingHomeContext();
  const [receiptTypeId, setReceiptTypeId] = useState<string | undefined>(
    undefined
  );
  const [tab, setTab] = useState<ReceiptBatchStatus | 'all'>('all');
  const [receiptDateRange, setReceiptDateRange] = useState<
    | {
        from?: Date;
        to?: Date;
      }
    | undefined
  >({
    from: dateFrom,
    to: dateTo,
  });
  const [submissionDateRange, setSubmissionDateRange] = useState<
    | {
        from?: Date;
        to?: Date;
      }
    | undefined
  >(undefined);
  const [amountRange, setAmountRange] = useState<
    | {
        from?: number;
        to?: number;
      }
    | undefined
  >(undefined);
  const [receiptCountRange, setReceiptCountRange] = useState<
    | {
        from?: number;
        to?: number;
      }
    | undefined
  >(undefined);

  const employeeIds = useSelectedEmployeeIds();

  const { data, isLoading } = useGetReceiptBatchListPhoenix(
    {
      params: {
        page: page + 1,
        page_size: pageSize,
        nursing_home_ids: selectedNursingHome?.id
          ? [selectedNursingHome.id]
          : [],
        created_by_employee_ids: employeeIds,
        order_by: ['updated_on'],
        order_directions: ['desc'],
        service_provider_name: query,
        receipt_type_ids: receiptTypeId ? [receiptTypeId] : undefined,
        service_provider_ids: serviceProviderIds,
        recurring_item_ids: recurringItemId ? [recurringItemId] : undefined,
        status:
          tab === 'all'
            ? undefined
            : tab === ReceiptBatchStatus.Submitted
            ? [
                ReceiptBatchJobState.Failed,
                ReceiptBatchJobState.Pending,
                ReceiptBatchJobState.Partial,
                ReceiptBatchJobState.Successful,
              ]
            : null,
        receipt_date_range: receiptDateRange
          ? [receiptDateRange.from, receiptDateRange.to]
          : undefined,
        submission_date_range: submissionDateRange
          ? [submissionDateRange.from, submissionDateRange.to]
          : undefined,
        total_amount_range: amountRange
          ? [amountRange.from, amountRange.to]
          : undefined,
        entry_count_range: receiptCountRange
          ? [receiptCountRange.from, receiptCountRange.to]
          : undefined,
      },
    },
    {
      placeholderData: keepPreviousData,
      enabled: selectedNursingHome?.id !== undefined,
    }
  );

  const [receiptImageIds, setReceiptImageIds] = useState<string[] | undefined>(
    undefined
  );
  const [imageGalleryTitle, setImageGalleryTitle] = useState<
    string | undefined
  >(undefined);

  const [openImageGallery, setOpenImageGallery] = useState(false);

  const { mutateAsync: generateReceiptBatch } = useGenerateReceiptBatchPreview(
    useErrorSnackbar().enqueueErrorSnackbar
  );

  const [generatedReceiptImageUrls, setGeneratedReceiptImageUrls] = useState<
    string[]
  >([]);
  const [generatedReceiptTypeMap, setGeneratedReceiptTypeMap] =
    useState<UrlTypeNameMapping>(new Map());

  const generateReceiptBatchPreview = useCallback(
    async (batchId: string, filename: string) => {
      try {
        const receiptBlob = await generateReceiptBatch({
          batchId,
        });
        if (receiptBlob) {
          const file = window.URL.createObjectURL(receiptBlob);

          setGeneratedReceiptImageUrls([file]);
          setGeneratedReceiptTypeMap(
            new Map([
              [
                file,
                {
                  type: 'application/pdf',
                  data: Promise.resolve(receiptBlob),
                  name: filename,
                },
              ],
            ])
          );
          setOpenImageGallery(true);
        }
      } catch {
        // handleFailedPreviewGenerationError(e);
      }
    },
    [generateReceiptBatch]
  );

  const onClickReceiptIcon = useCallback(
    ({
      id,
      receiptImageIds,
      title,
      generateReceipts,
    }: {
      id: string;
      receiptImageIds: string[] | undefined;
      title: string;
      generateReceipts: boolean;
    }) => {
      if (generateReceipts) {
        generateReceiptBatchPreview(id, title); // Title contains the filename when in preview mode
        return;
      }
      setReceiptImageIds(receiptImageIds);
      setImageGalleryTitle(title);
      if (receiptImageIds && receiptImageIds.length > 0) {
        setOpenImageGallery(true);
      }
    },
    [generateReceiptBatchPreview]
  );

  return (
    <>
      <ReceiptBatchOverviewTableView
        data={data?.data ?? []}
        query={query}
        onChangeQuery={setQuery}
        receiptTypeId={receiptTypeId}
        setReceiptTypeId={setReceiptTypeId}
        totalRowCount={data?.meta?.total_count ?? 0}
        page={page}
        pageSize={pageSize}
        onChangePage={setPage}
        onChangePageSize={setPageSize}
        isLoading={isLoading}
        onClickReceiptIcon={onClickReceiptIcon}
        filters={filters ?? []}
        receiptDateRange={receiptDateRange}
        onChangeReceiptDateRange={setReceiptDateRange}
        submissionDateRange={submissionDateRange}
        onChangeSubmissionDateRange={setSubmissionDateRange}
        amountRange={amountRange}
        onChangeAmountRange={setAmountRange}
        receiptCountRange={receiptCountRange}
        onChangeReceiptCountRange={setReceiptCountRange}
        DropdownComponent={showReceiptTypeDropdown ? ReceiptTypeDropdown : null}
        tab={tab}
        setTab={setTab}
      />
      <ReceiptBatchTableEnlargeGallery
        openImageGallery={openImageGallery}
        setOpenImageGallery={setOpenImageGallery}
        batchImageIds={receiptImageIds}
        title={imageGalleryTitle ?? ''}
        generatedReceiptImageUrls={generatedReceiptImageUrls}
        setGeneratedReceiptImageUrls={setGeneratedReceiptImageUrls}
        generatedReceiptTypeMap={generatedReceiptTypeMap}
      />
    </>
  );
};
